import React, { useState, useEffect } from 'react';
import { Loader2, Upload, Trash2, Video as VideoIcon } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import MultiCategorySelect from './MultiCategorySelect';
import TranslateButton from './TranslateButton';
import LessonsManager from './LessonsManager';
import ColorPicker from './ColorPicker';
import PostSectionsEditor from './PostSectionsEditor';
import { RichTextEditor } from './RichTextEditor';
import { FreeToggle } from './FreeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic edit dialog for posts, videos, courses
 * type: 'post' | 'video' | 'course'
 */
const EditDialog = ({ type, item, open, onClose, onUpdated }) => {
  const { lang } = useLang();
  const { toast } = useToast();
  const [form, setForm] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoFileName, setVideoFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const L = (ar, en) => tx(lang, ar, en);

  useEffect(() => {
    if (item) {
      setForm({
        ...item,
        categories: Array.isArray(item.categories) && item.categories.length > 0
          ? item.categories
          : (item.category ? [item.category] : ['general']),
      });
      setImageFile(null); setImagePreview('');
      setVideoFile(null); setVideoFileName('');
      setProgress(0);
    }
  }, [item]);

  if (!item) return null;

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const translateFields = {
    post: [
      { source: 'titleAr', target: 'titleEn' },
      { source: 'contentAr', target: 'contentEn' },
      { source: 'excerptAr', target: 'excerptEn' },
    ],
    video: [
      { source: 'titleAr', target: 'titleEn' },
      { source: 'descAr', target: 'descEn' },
    ],
    course: [
      { source: 'titleAr', target: 'titleEn' },
      { source: 'brandAr', target: 'brandEn' },
      { source: 'subtitleAr', target: 'subtitleEn' },
      { source: 'levelAr', target: 'levelEn' },
      { source: 'descriptionAr', target: 'descriptionEn' },
    ],
  }[type] || [];
  const translateFieldsReverse = translateFields.map(f => ({ source: f.target, target: f.source }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      const include = {
        post: ['titleAr', 'titleEn', 'contentAr', 'contentEn', 'excerptAr', 'excerptEn'],
        video: ['titleAr', 'titleEn', 'descAr', 'descEn'],
        course: ['titleAr', 'titleEn', 'brandAr', 'brandEn', 'subtitleAr', 'subtitleEn', 'levelAr', 'levelEn', 'descriptionAr', 'descriptionEn', 'color', 'youtube'],
      }[type];
      include.forEach(k => {
        if (form[k] !== undefined && form[k] !== null) fd.append(k, form[k]);
      });
      fd.append('is_free', form.is_free ? 'true' : 'false');
      // Multi-categories (post, video, course)
      if ((type === 'post' || type === 'video' || type === 'course') && Array.isArray(form.categories)) {
        const cats = form.categories.length > 0 ? form.categories : ['general'];
        fd.append('categories', cats.join(','));
      }
      if (imageFile && (type === 'post' || type === 'course')) fd.append('image', imageFile);
      if (videoFile && type === 'course') fd.append('video', videoFile);
      if (videoFile && type === 'post') fd.append('video', videoFile);

      const url = `/${type}s/${item.id}`;
      const { data } = await api.patch(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      onUpdated?.(data);
      toast({ title: L('تم التعديل بنجاح', 'Updated successfully') });
      onClose?.();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const onImage = (e) => {
    const f = e.target.files?.[0];
    if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
  };
  const onVideo = (e) => {
    const f = e.target.files?.[0];
    if (f) { setVideoFile(f); setVideoFileName(f.name); }
  };

  const titleByType = {
    post: L('تعديل المنشور', 'Edit Post'),
    video: L('تعديل الفيديو', 'Edit Video'),
    course: L('تعديل الكورس', 'Edit Course'),
  };

  const currentCover = imagePreview || (form.cover ? (typeof form.cover === 'string' && form.cover.startsWith('/api/') ? `${BACKEND_URL}${form.cover}` : form.cover) : '');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900 text-xl">{titleByType[type]}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* Common: titles */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={form.titleAr || ''} onChange={e => upd('titleAr', e.target.value)} placeholder={L('العنوان بالعربية', 'Title Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
            <Input value={form.titleEn || ''} onChange={e => upd('titleEn', e.target.value)} placeholder={L('العنوان بالإنجليزية', 'Title English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
          </div>

          <div className="flex flex-wrap gap-2">
            <TranslateButton
              sourceLang="ar"
              targetLang="en"
              getValues={() => form}
              setValue={(k, v) => upd(k, v)}
              fields={translateFields}
              testId={`edit-${type}-translate-ar-en`}
            />
            <TranslateButton
              sourceLang="en"
              targetLang="ar"
              getValues={() => form}
              setValue={(k, v) => upd(k, v)}
              fields={translateFieldsReverse}
              testId={`edit-${type}-translate-en-ar`}
            />
          </div>

          {type === 'course' && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={form.brandAr || ''} onChange={e => upd('brandAr', e.target.value)} placeholder={L('العلامة التجارية بالعربية', 'Brand Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                <Input value={form.brandEn || ''} onChange={e => upd('brandEn', e.target.value)} placeholder={L('العلامة التجارية بالإنجليزية', 'Brand English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={form.subtitleAr || ''} onChange={e => upd('subtitleAr', e.target.value)} placeholder={L('عنوان فرعي بالعربية', 'Subtitle Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                <Input value={form.subtitleEn || ''} onChange={e => upd('subtitleEn', e.target.value)} placeholder={L('عنوان فرعي بالإنجليزية', 'Subtitle English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={form.levelAr || ''} onChange={e => upd('levelAr', e.target.value)} placeholder={L('المستوى بالعربية', 'Level Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                <Input value={form.levelEn || ''} onChange={e => upd('levelEn', e.target.value)} placeholder={L('المستوى بالإنجليزية', 'Level English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
              </div>
              <div className="grid gap-3">
                <div>
                  <ColorPicker
                    value={form.color || 'blue'}
                    onChange={(c) => upd('color', c)}
                    testId="edit-course-color-picker"
                  />
                </div>
              </div>
              <div>
                <Input value={form.youtube || ''} onChange={e => upd('youtube', e.target.value)} placeholder="YouTube URL" className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
              </div>
            </>
          )}

          {/* Cover media (post & course). Post allows image OR video; course keeps image */}
          {(type === 'post' || type === 'course') && (
            <div>
              <label className="text-slate-600 text-sm mb-2 block">
                {type === 'post' ? L('تغيير الغلاف — صورة أو فيديو (اختياري)', 'Change cover — image or video (optional)') : L('تغيير الصورة (اختياري)', 'Change image (optional)')}
              </label>
              {currentCover && (
                imageFile ? null : (videoFile ? null : (
                  /\.(mp4|mov|webm|m4v)(\?|$)/i.test(currentCover) || currentCover.includes('/video/upload/') || currentCover.includes('/api/uploads/videos/') ? (
                    <video src={currentCover} controls className="w-full max-h-48 rounded-lg mb-2 border border-slate-200 bg-black" />
                  ) : (
                    <img src={currentCover} alt="" className="w-full max-h-48 object-cover rounded-lg mb-2 border border-slate-200" />
                  )
                ))
              )}
              {imageFile && <img src={imagePreview} alt="" className="w-full max-h-48 object-cover rounded-lg mb-2 border border-slate-200" />}
              {videoFile && type === 'post' && <video src={URL.createObjectURL(videoFile)} controls className="w-full max-h-48 rounded-lg mb-2 border border-slate-200 bg-black" />}
              <div className={type === 'post' ? 'grid sm:grid-cols-2 gap-3' : 'block'}>
                <label className="block">
                  <div className="rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-300 transition-colors p-4 text-center cursor-pointer bg-slate-50">
                    <Upload className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <div className="text-slate-600 text-xs">{imageFile ? imageFile.name : L('اختر صورة', 'Choose image')}</div>
                    <input type="file" accept="image/*" onChange={onImage} className="hidden" />
                  </div>
                </label>
                {type === 'post' && (
                  <label className="block">
                    <div className="rounded-xl border-2 border-dashed border-purple-900/60 hover:border-purple-500/60 transition-colors p-4 text-center cursor-pointer bg-slate-50">
                      <VideoIcon className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                      <div className="text-slate-600 text-xs">{videoFile ? videoFile.name : L('اختر فيديو', 'Choose video')}</div>
                      <input type="file" accept="video/*" onChange={onVideo} className="hidden" />
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Video upload (course only) */}
          {type === 'course' && (
            <div>
              <label className="text-slate-600 text-sm mb-2 block">{L('تغيير الفيديو (اختياري)', 'Change video (optional)')}</label>
              <label className="block">
                <div className="rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-300 transition-colors p-4 text-center cursor-pointer bg-slate-50">
                  <VideoIcon className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <div className="text-slate-600 text-xs">{videoFileName || L('اضغط لرفع فيديو جديد', 'Click to upload new video')}</div>
                  <input type="file" accept="video/*" onChange={onVideo} className="hidden" />
                </div>
              </label>
            </div>
          )}

          {/* Content / description */}
          {type === 'post' && (
            <>
              <div className="space-y-3">
                <RichTextEditor key={`edit-c-ar-${item.id}`} testId="edit-post-content-ar" dir="rtl" minHeight={220} value={form.contentAr || ''} onChange={v => upd('contentAr', v)} placeholder={L('المحتوى بالعربية', 'Content Arabic')} />
                <RichTextEditor key={`edit-c-en-${item.id}`} testId="edit-post-content-en" dir="ltr" minHeight={220} value={form.contentEn || ''} onChange={v => upd('contentEn', v)} placeholder={L('المحتوى بالإنجليزية', 'Content English')} />
              </div>
              <div>
                <label className="text-slate-600 text-sm mb-2 block">{L('التصنيفات (يمكن اختيار أكثر من واحد)', 'Categories (multiple allowed)')}</label>
                <MultiCategorySelect
                  value={form.categories || []}
                  onChange={(arr) => upd('categories', arr)}
                  dataTestId="edit-post-categories"
                />
              </div>
              {(item.sections || []).length > 0 && <PostSectionsEditor
                postId={item.id}
                sections={form.sections || []}
                onChange={(sections) => upd('sections', sections)}
              />}
            </>
          )}

          {type === 'video' && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Textarea value={form.descAr || ''} onChange={e => upd('descAr', e.target.value)} rows={3} placeholder={L('الوصف بالعربية', 'Description Arabic')} className="bg-slate-50 border-slate-200 text-slate-900" />
                <Textarea value={form.descEn || ''} onChange={e => upd('descEn', e.target.value)} rows={3} placeholder={L('الوصف بالإنجليزية', 'Description English')} className="bg-slate-50 border-slate-200 text-slate-900" dir="ltr" />
              </div>
              <div>
                <label className="text-slate-600 text-sm mb-2 block">{L('التصنيفات (يمكن اختيار أكثر من واحد)', 'Categories (multiple allowed)')}</label>
                <MultiCategorySelect
                  value={form.categories || []}
                  onChange={(arr) => upd('categories', arr)}
                  dataTestId="edit-video-categories"
                />
              </div>
            </>
          )}

          {type === 'course' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Textarea value={form.descriptionAr || ''} onChange={e => upd('descriptionAr', e.target.value)} rows={3} placeholder={L('الوصف بالعربية', 'Description Arabic')} className="bg-slate-50 border-slate-200 text-slate-900" />
              <Textarea value={form.descriptionEn || ''} onChange={e => upd('descriptionEn', e.target.value)} rows={3} placeholder={L('الوصف بالإنجليزية', 'Description English')} className="bg-slate-50 border-slate-200 text-slate-900" dir="ltr" />
            </div>
          )}

          {type === 'course' && (
            <div>
              <label className="text-slate-600 text-sm mb-2 block">{L('التصنيفات (يمكن اختيار أكثر من واحد)', 'Categories (multiple allowed)')}</label>
              <MultiCategorySelect
                value={form.categories || []}
                onChange={(arr) => upd('categories', arr)}
                dataTestId="edit-course-categories"
              />
            </div>
          )}

          <div className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
            <span className="text-slate-700 text-sm font-semibold">{L('حالة المحتوى:', 'Access:')}</span>
            <FreeToggle value={!!form.is_free} onChange={(val) => upd('is_free', val)} testId="edit-free-toggle" />
            <span className="text-slate-500 text-xs">{form.is_free ? L('متاح لأي زائر', 'Open to every visitor') : L('للمشتركين فقط', 'Subscribers only')}</span>
          </div>

          {type === 'course' && (
            <LessonsManager
              courseId={item.id}
              lessons={form.lessons || []}
              onChange={(newLessons) => upd('lessons', newLessons)}
            />
          )}

          {busy && progress > 0 && (
            <div className="w-full bg-slate-50 rounded-full h-2 overflow-hidden"><div className="bg-blue-500 h-full transition-all" style={{ width: `${progress}%` }} /></div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={busy} data-testid="edit-save-btn" className="btn-primary-blue border-0 h-11 px-7 font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {L('حفظ التعديلات', 'Save Changes')}
            </Button>
            <Button type="button" onClick={onClose} data-testid="edit-cancel-btn" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 h-11">
              {L('إلغاء', 'Cancel')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditDialog;
